/**
 * Platform Detection Utilities
 *
 * Provides robust platform detection for displaying correct modifier keys
 * and other platform-specific UI elements.
 *
 * Note: navigator.platform is deprecated, so we use userAgent as a fallback.
 */

/**
 * Detect if the current platform is macOS, iOS, or iPadOS.
 * Uses userAgent as primary check since navigator.platform is deprecated.
 */
export const isMac: boolean = (() => {
  if (typeof navigator === "undefined") return false;

  // Check userAgent first (more reliable and not deprecated)
  const ua = navigator.userAgent;
  if (ua.includes("Mac") || ua.includes("iPhone") || ua.includes("iPad") || ua.includes("iPod")) {
    return true;
  }

  // Fallback to platform for older browsers (deprecated but still works)
  const platform = navigator.platform ?? "";
  return /Mac|iPhone|iPad|iPod/.test(platform);
})();

/**
 * The modifier key name to display in UI (Cmd on Mac, Ctrl elsewhere).
 */
export const modKey: string = isMac ? "Cmd" : "Ctrl";

/**
 * The modifier key symbol to display in UI.
 * Uses the Unicode command symbol on Mac.
 */
export const modSymbol: string = isMac ? "\u2318" : "Ctrl";

/**
 * Check if the user prefers reduced motion.
 * This function checks the current state and should be called at the time of use,
 * not cached, since the preference can change.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Get the appropriate scroll behavior based on user preference.
 * Returns "auto" if the user prefers reduced motion, otherwise "smooth".
 */
export function getScrollBehavior(): ScrollBehavior {
  return prefersReducedMotion() ? "auto" : "smooth";
}
